// @format

import { Litepicker } from "litepicker";
import "litepicker/dist/plugins/ranges";
import "litepicker/dist/css/plugins/ranges.js.css";
import { showNotice } from "./notice";

const FORM = "form.interested-filters";
const BUTTON = ".interested-export-btn";
const MAX_POLLS = 150; // ~5 minutes at 2s between polls
const ALL_TIME_FROM = new Date(1970, 0, 1);

let bound = false;
const pickers = new WeakMap<HTMLElement, Litepicker>();

function isoDate(date: { format: (pattern: string) => string }): string {
  return date.format("YYYY-MM-DD");
}

// The presets mirror the server's ranges (EmailResponsesController#interested_export_range):
// "Last N days" runs from N days ago to today.
// An array, not an object: integer-like keys ("7") would sort ahead of "all" and reorder the list.
function presets(): { value: string; label: string; range: [Date, Date] }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysAgo = (n: number): Date => new Date(today.getFullYear(), today.getMonth(), today.getDate() - n);
  return [
    { value: "all", label: "All time", range: [ALL_TIME_FROM, today] },
    { value: "7", label: "Last 7 days", range: [daysAgo(7), today] },
    { value: "14", label: "Last 14 days", range: [daysAgo(14), today] },
    { value: "30", label: "Last 30 days", range: [daysAgo(30), today] },
  ];
}

// A preset is sent by name, so the server keeps it rolling; any other pick is a custom range.
function applyRange(form: HTMLFormElement, from: string, to: string): void {
  const toIso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const preset = presets().find((p) => toIso(p.range[0]) === from && toIso(p.range[1]) === to);

  form.querySelector<HTMLInputElement>("#interested-range")!.value = preset ? preset.value : "custom";
  form.querySelector<HTMLInputElement>("#interested-range-from")!.value = preset ? "" : from;
  form.querySelector<HTMLInputElement>("#interested-range-to")!.value = preset ? "" : to;
  form.requestSubmit();
}

function ensurePicker(trigger: HTMLElement): Litepicker {
  const existing = pickers.get(trigger);
  if (existing) return existing;

  const form = trigger.closest("form") as HTMLFormElement;
  const customRanges: Record<string, [Date, Date]> = {};
  presets().forEach((p) => { customRanges[p.label] = p.range; });
  // startDate/endDate rather than setDateRange(): that emits "selected", which would resubmit at once.
  const picker = new Litepicker({
    element: trigger,
    singleMode: false,
    autoApply: true,
    maxDate: new Date(),
    format: "YYYY-MM-DD",
    startDate: trigger.dataset.from || undefined,
    endDate: trigger.dataset.to || undefined,
    dropdowns: { minYear: 1970, maxYear: new Date().getFullYear(), months: true, years: true },
    numberOfMonths: 1,
    numberOfColumns: 1,
    plugins: ["ranges"],
    ranges: { customRanges },
    setup: (p) => {
      p.on("selected", (start, end) => applyRange(form, isoDate(start), isoDate(end)));
    },
  });
  pickers.set(trigger, picker);
  return picker;
}

type ExportState = "idle" | "preparing" | "ready";

function setState(button: HTMLButtonElement, state: ExportState): void {
  const label = button.querySelector<HTMLElement>(".interested-export-label")!;
  button.dataset.state = state;
  button.disabled = state === "preparing";
  button.classList.toggle("is-exporting", state === "preparing");
  button.classList.toggle("is-ready", state === "ready");
  label.textContent = { idle: "Export file", preparing: "Preparing…", ready: "Download file" }[state];
}

function pollForCsv(token: string, statusUrl: string, done: (ok: boolean) => void): void {
  let attempts = 0;

  const tick = (): void => {
    attempts += 1;
    if (attempts > MAX_POLLS) {
      showNotice("The CSV is taking longer than expected. Please try again shortly.", "danger");
      done(false);
      return;
    }

    fetch(`${statusUrl}?token=${encodeURIComponent(token)}`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: { ready?: boolean }) => {
        if (data.ready) done(true);
        else setTimeout(tick, 2000);
      })
      .catch(() => setTimeout(tick, 2000));
  };

  setTimeout(tick, 1500);
}

// The export takes the filters the list is showing, i.e. the ones in the URL, not unsubmitted UI state.
function startExport(button: HTMLButtonElement): void {
  const body = new URLSearchParams(window.location.search);
  body.delete("page");
  if (!body.get("range")) body.set("range", "all");

  // The header carries the CSRF token. A posted authenticity_token is single-use here
  // (ApplicationController#store_used_csrf_token), so a second export in the same page would be
  // redirected back to the page, and fetch follows that as a GET.
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";
  const fail = (message?: string): void => {
    showNotice(message || "Could not start the CSV export. Please try again.", "danger");
    setState(button, "idle");
  };

  setState(button, "preparing");
  fetch(button.dataset.exportUrl || "", {
    method: "POST",
    headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
    body,
  })
    .then((res) =>
      res.json().then((data: { token?: string; error?: string }) => {
        if (!res.ok || !data.token) {
          fail(data.error);
          return;
        }

        const token = data.token;
        pollForCsv(token, button.dataset.statusUrl || "", (ok) => {
          if (!ok) {
            setState(button, "idle");
            return;
          }
          button.dataset.token = token;
          setState(button, "ready");
        });
      })
    )
    .catch(() => fail());
}

export function initInterestedExport(): void {
  if (bound) return;
  bound = true;

  document.addEventListener("click", (event) => {
    const target = event.target as Element;
    // created on first use; Litepicker opens itself on later clicks of its element
    const trigger = target.closest?.<HTMLElement>(`${FORM} .ir-date`);
    if (trigger && !pickers.has(trigger)) {
      ensurePicker(trigger).show(trigger);
      return;
    }

    const button = target.closest?.<HTMLButtonElement>(BUTTON);
    if (!button) return;

    if (button.dataset.state === "ready") {
      window.location.href = `${button.dataset.downloadUrl}?token=${encodeURIComponent(button.dataset.token || "")}`;
      setState(button, "idle");
    } else if (button.dataset.state !== "preparing") {
      startExport(button);
    }
  });
}
